import { useTranslation } from 'react-i18next';
import { Card } from '#pie/components/card/Card';
import { DescriptionList } from '#pie/components/description-list/DescriptionList';
import { DescriptionListItem } from '#pie/components/description-list/description-list-item/DescriptionListItem';
import { InfoTooltip } from '#pie/components/info-tooltip/InfoTooltip';
import { Stack } from '#pie/components/stack/Stack';
import { StatusTag } from '#pie/components/status-tag/StatusTag';
import { Text } from '#pie/components/text/Text';
import { getFormattedDate } from '#pie/i18n/dateFormats';
import type { MarketPartyRegistration } from './marketPartyRegistrationAdapter';

interface MarketPartySubRegistrationProps {
  marketParty: MarketPartyRegistration;
}

export const MarketPartySubRegistration = ({
  marketParty,
}: MarketPartySubRegistrationProps) => {
  const { t } = useTranslation();
  const { t: tmp } = useTranslation('marketParty');

  return (
    <Card
      size="lg"
      heading={
        <Stack direction="row" gap="sm" align="center">
          <Text as="h2" variant="h6" className="text-content-heading">
            {t('common.subregistration.title')}
          </Text>
          <InfoTooltip
            className="text-content-heading"
            aria-label={tmp('sub_registration.aria-label')}
            size={20}
          >
            {tmp('sub_registration.title.tooltip')}
          </InfoTooltip>
        </Stack>
      }
      headingUnderline={false}
    >
      {marketParty.subUnits
        ?.sort((a, b) => {
          if (a.eanCode === marketParty.marketParticipantEanCode) return -1;
          if (b.eanCode === marketParty.marketParticipantEanCode) return 1;
          return 0;
        })
        .map((subUnit, index) => (
          <Stack gap="sm" className="mb-4" key={subUnit.mrId}>
            <Card
              key={index}
              size="md"
              hasBorder="base"
              heading={
                <Stack
                  direction="row"
                  align="center"
                  className="justify-between"
                >
                  <Text
                    as="h2"
                    variant="base"
                    className="text-content-heading font-bold"
                  >
                    {subUnit.name}
                  </Text>
                  {subUnit.eanCode === marketParty.marketParticipantEanCode && (
                    <StatusTag status="closed">
                      {tmp('primary_subregistration.tag')}
                    </StatusTag>
                  )}
                </Stack>
              }
            >
              <DescriptionList variant="grid">
                <DescriptionListItem
                  direction="vertical"
                  title={tmp('sub_registration.name')}
                >
                  {subUnit.name}
                </DescriptionListItem>
                <DescriptionListItem
                  direction="vertical"
                  title={tmp('sub_registration.eancode')}
                >
                  {subUnit.eanCode}
                </DescriptionListItem>
                <DescriptionListItem
                  direction="vertical"
                  title={tmp('sub_registration.vat')}
                >
                  {subUnit.vat || '-'}
                </DescriptionListItem>
                <DescriptionListItem
                  direction="vertical"
                  title={tmp('sub_registration.iban')}
                >
                  {subUnit.iban || '-'}
                </DescriptionListItem>
                <DescriptionListItem
                  direction="vertical"
                  title={tmp('startdate')}
                >
                  {subUnit.startDate &&
                    getFormattedDate(new Date(subUnit.startDate), false)}
                </DescriptionListItem>
                <DescriptionListItem
                  direction="vertical"
                  title={tmp('enddate')}
                >
                  {(subUnit.endDate &&
                    getFormattedDate(new Date(subUnit.endDate), false)) ||
                    tmp('license.title_indefinite')}
                </DescriptionListItem>
                {subUnit.appearances?.some((appearance) =>
                  ['EGC', 'P2P', 'ACP', 'IAC', 'LVR'].includes(appearance),
                ) && (
                  <DescriptionListItem
                    direction="vertical"
                    title={tmp('sub_registration.specific_form')}
                  >
                    {subUnit.appearances
                      .filter((appearance) =>
                        ['EGC', 'P2P', 'ACP', 'IAC', 'LVR'].includes(
                          appearance,
                        ),
                      )
                      .map((appearance) => {
                        if (appearance === 'LVR') {
                          return tmp(
                            'sub_registration.specific_form.regular_supplier',
                          );
                        }
                        return tmp(
                          `provided_service.abbreviations_${appearance}`,
                          {
                            defaultValue: '',
                          },
                        );
                      })
                      .join(', ')}
                  </DescriptionListItem>
                )}
              </DescriptionList>
            </Card>
          </Stack>
        ))}
    </Card>
  );
};
